import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AiOutlineClose,
  AiOutlineLogout,
  AiOutlineSearch,
  AiOutlineShoppingCart,
  AiOutlineUser,
} from 'react-icons/ai';
import { IoMdArrowDropdown } from 'react-icons/io';
import { RxDashboard } from 'react-icons/rx';
import { GiHamburgerMenu } from 'react-icons/gi';
import axios from 'axios';

import { setisLoggedin, setdata } from '../redux/reducers/userSlice';
import styles from '../styles/navbar.module.css';

function SearchForm({ value, onChange, onSubmit, autoFocus, className }) {
  return (
    <form className={className} onSubmit={onSubmit}>
      <AiOutlineSearch className={styles.searchIcon} />
      <input
        type="search"
        value={value}
        onChange={onChange}
        placeholder="Search bikes, parts and gear..."
        aria-label="Search products"
        autoFocus={autoFocus}
      />
      <button type="submit">Search</button>
    </form>
  );
}

function Navbar() {
  const { users, cart } = useSelector((state) => state);

  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [search, setSearch] = useState('');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoggedIn = users?.isloggedin;
  const userData = users?.userdata;
  const cartCount = cart?.cart?.length || 0;

  const handleSearch = (event) => {
    event.preventDefault();
    const query = search.trim();

    if (!query) {
      toast.error('Please enter a product name to search');
      return;
    }

    navigate(`/search/${encodeURIComponent(query)}`);
    setMobileSearchOpen(false);
  };

  const handleLogout = async () => {
    try {
      const response = await axios.post('https://pro-shop-backend.vercel.app/api/users/logout');

      if (response.status === 200 || response.status === 201 || response.status === 204) {
        toast.success(response.data?.message || 'Logged out successfully', {
          position: 'bottom-right',
          theme: 'colored',
        });
      } else {
        toast.success(response.data?.message || 'Logged out successfully', {
          position: 'bottom-right',
          theme: 'colored',
        });
      }

      dispatch(setisLoggedin(false));
      dispatch(setdata());
      setProfileOpen(false);
      navigate('/login');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Logout failed. Please try again.';
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored',
      });
    }
  };

  return (
    <header className={styles.navbar}>
      <div className={styles.container}>
        <button type="button" className={styles.logo} onClick={() => navigate('/')} aria-label="Go to homepage">
          <span className={styles.logoMark}>P</span>
          <span className={styles.logoText}>
            <strong>PROSHOP</strong>
            <small>RIDE FURTHER</small>
          </span>
        </button>

        <SearchForm
          className={styles.searchForm}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSubmit={handleSearch}
        />

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cartButton}
            onClick={() => navigate('/cart')}
            aria-label={`Shopping cart with ${cartCount} item(s)`}
          >
            <span className={styles.cartIconWrapper}>
              <AiOutlineShoppingCart />
              {cartCount > 0 && <span className={styles.cartBadge}>{cartCount}</span>}
            </span>
            <span className={styles.actionLabel}>Cart</span>
          </button>

          {isLoggedIn ? (
            <div className={styles.profileWrapper}>
              <button
                type="button"
                className={styles.profileButton}
                onClick={() => setProfileOpen(!profileOpen)}
                aria-expanded={profileOpen}
              >
                <span className={styles.userAvatar}>
                  {userData?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
                <span className={styles.userName}>{userData?.name || 'My Account'}</span>
                <IoMdArrowDropdown className={`${styles.dropdownIcon} ${profileOpen ? styles.dropdownIconOpen : ''}`} />
              </button>

              {profileOpen && (
                <div className={styles.profileMenu}>
                  <div className={styles.profileMenuHeader}>
                    <strong>{userData?.name || 'User'}</strong>
                    <span>{userData?.email || 'Welcome back'}</span>
                  </div>

                  <button type="button" onClick={() => { setProfileOpen(false); navigate('/myprofile'); }}>
                    <AiOutlineUser />
                    My Profile
                  </button>
                  {userData?.isAdmin === true && (
                    <button type="button" onClick={() => { setProfileOpen(false); navigate('/admin'); }}>
                      <RxDashboard />
                      Admin Dashboard
                    </button>
                  )}

                  <div className={styles.menuDivider} />

                  <button type="button" className={styles.logoutButton} onClick={handleLogout}>
                    <AiOutlineLogout />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button type="button" className={styles.signinButton} onClick={() => navigate('/login')}>
              <AiOutlineUser />
              <span>Sign in</span>
            </button>
          )}

          <button
            type="button"
            className={styles.mobileSearchToggle}
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
            aria-label="Open search"
          >
            {mobileSearchOpen ? <AiOutlineClose /> : <GiHamburgerMenu />}
          </button>
        </div>
      </div>

      {mobileSearchOpen && (
        <SearchForm
          className={styles.mobileSearch}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSubmit={handleSearch}
          autoFocus
        />
      )}
    </header>
  );
}

export default Navbar;